import { Grid, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import {
  colors,
  CreditCardData,
  DefaultButton,
  MessageBox,
} from 'design-system';
import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Data Layer
import {
  appliesAutoPxDiscount,
  appliesAutoPxForPayment,
  defaultUnit,
} from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  ProductConfiguration,
  Quotation,
} from 'data-layer-cr/dist/models';

// Redux Related
import { requestYappyPayment } from 'src/store/emissionPolicy/actions';

import { requestCoverageCatalogs } from 'src/store/coverageCatalog/actions';
import {
  PaymentDataFields,
  TotalsPayload,
} from 'src/store/emissionPolicy/slice/emissionPolicySlice';
import { RootState } from 'src/store/rootReducer';

// Models
import ACHData from 'src/models/helperTypes/ACHData';
import EmissionDataFields, {
  LegalBeneficiary,
} from 'src/models/helperTypes/EmissionDataFields';
import FinalBeneficiary from 'src/models/helperTypes/FinalBeneficiary';

// Constants
import { profiles } from 'src/constants/profileTypes';

// Components
import DynamicQuotationCard from '../../components/DynamicQuotationCard/DynamicQuotationCard';
import SnackbarMessage from '../../components/SnackbarMessage';
import useBaseWrapperStyles from '../useBaseWrapperStyles';
import BeneficiariesDataCard from './BeneficiariesDataCard';
import BeneficiarySummaryCard from './BeneficiarySummaryCard';
import CustomerDataCard from './CustomerDataCard';
import DownloadDocumentButton from './DownloadDocumentButton';
import FullVehicleDataCard from './FullVehicleDataCard';
import PackageCard from './PackageCard';
import PaymentSummaryCard from './PaymentSummaryCard';

const useStyle = makeStyles(() => ({
  copyLinkMessage: {
    fontWeight: 400,
    fontSize: 15,
    paddingRight: 20,
    lineHeight: '22px',
  },
  spaceLinkText: {
    marginLeft: 10,
  },
}));

/**
 * The message to copy to clipboard
 */
export const signatureEmissionMessage = (link: string): string =>
  `Recuerda que debes realizar la certificación digital con el cliente para poder emitirle la póliza del auto.
  \n
  Puedes realizar la misma desde este link: ${link}`;

export const getSignButtonText = (isColectiveQuotation: boolean): string =>
  isColectiveQuotation
    ? 'Certificar y Emitir Póliza'
    : 'Continuar a Certificación';

export const submitButton = (
  submit: () => void,
  isColectiveQuotation: boolean,
): React.ReactNode => (
  <DefaultButton
    id="sales-web-inspection-button-buypolicy"
    data-testid="submit-button"
    fullWidth
    onClick={submit}
    customColor={{
      default: colors.bright_turquoise,
      hover: colors.onahau,
      active: colors.white,
    }}
  >
    {getSignButtonText(isColectiveQuotation)}
  </DefaultButton>
);

export const Summary = ({
  // Data
  paymentDataFields,
  customerData,
  finalBeneficiaryData,
  profileExists,
  quotation,
  legalBeneficiary,
  coverageCatalogs,
  creditCardData,
  achData, // recorded totals from payment step
  maxAllowedPayments,

  // Actions
  requestCoverageCatalogsAction,

  // OnSubmit
  onSubmit,

  // Other props
  collapsibleCards = false,
  isOptionsDetails = false,
  optionSelected = 0,
}: Props): JSX.Element => {
  const theme = useTheme();
  const classes = useStyle();
  const wrapperClasses = useBaseWrapperStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openCopySnack, setOpenCopySnack] = React.useState(false);

  // Scroll to the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /** The used unit */
  /* Change to filter Unit Array and get Option Selected by user */
  const clonedQuotation = {
    ...quotation,
    packageOption:
      optionSelected !== 0 ? optionSelected : quotation.packageOption,
  };
  const usedQuotationUnit = useMemo(() => {
    return defaultUnit(clonedQuotation);
  }, [clonedQuotation]);

  useEffect(() => {
    if (coverageCatalogs.length <= 0) {
      requestCoverageCatalogsAction(
        quotation.productId,
        quotation.user.id,
        quotation.quotationProfile.id,
        quotation.units[0].unit.use,
      );
    }
  }, [requestCoverageCatalogsAction, quotation, coverageCatalogs.length]);

  // Note that 'collapsibleCards" is actually doing the reverse of what it names suggests...
  const cardsShouldNotCollapse = collapsibleCards || !isMobile;

  /** Beneficiary Summary Card should only be displayed if customer is a legal entity and its profile does not exist in Acsel */
  const showBeneficiary =
    !isOptionsDetails && quotation.customerType === 'J' && !profileExists;

  const { type } = paymentDataFields;

  const isColectiveQuotation =
    quotation.quotationProfile.type === profiles.COLLECTIVE;

  // Check if quotation has freeMoth (is autoPX)
  const hasFreeMonth = appliesAutoPxForPayment(quotation, type);

  // Copy link action logic
  const handleCopyLinkClipboard = (): void => {
    const baseAppUrl = `${process.env.REACT_APP_INSPECTION_URL}`;
    const link = `${baseAppUrl}${quotation.urlSalt}`;
    const clipboardMessage = signatureEmissionMessage(link);
    copy(clipboardMessage);
    setOpenCopySnack(true);
  };

  return (
    <Grid container justifyContent="center">
      <Grid
        container
        spacing={2}
        item
        xs={12}
        className={wrapperClasses.pageContainer}
      >
        <Grid item xs={12}>
          <MessageBox
            data-testid="warning-box"
            variant="info"
            message="Verifica la información a continuación"
            hideCloseButton
          />
        </Grid>

        {!isOptionsDetails && isColectiveQuotation && (
          <Grid item xs={12}>
            <PaymentSummaryCard
              data-testid="payment-summary"
              creditCardData={creditCardData}
              achData={achData}
              paymentFields={paymentDataFields}
              paymentTotal={quotation.summary.anualPayment}
              hasFreeMonth={hasFreeMonth}
              quotation={quotation}
              validityStartDate={customerData.effectiveDate}
            />
          </Grid>
        )}

        {!isColectiveQuotation && (
          <Grid item xs={12}>
            <PackageCard
              data-testid="summary-package-card"
              quotation={quotation}
            />
          </Grid>
        )}

        {!isOptionsDetails && (
          <Grid item xs={12}>
            <CustomerDataCard
              data-testid="customer-data-card"
              customerType={quotation.customerType}
              dataFields={customerData}
              preventCollapse={cardsShouldNotCollapse}
              isCollective={isColectiveQuotation}
            />
          </Grid>
        )}

        {showBeneficiary && (
          <Grid item xs={12}>
            <BeneficiarySummaryCard
              data-testid="beneficiary-data-card"
              beneficiary={finalBeneficiaryData}
              preventCollapse={cardsShouldNotCollapse}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FullVehicleDataCard
            data-testid="full-vehicle-data-card"
            summary={quotation.summary}
            preventCollapse={cardsShouldNotCollapse}
            isOptionsDetails={collapsibleCards}
          />
        </Grid>
        {coverageCatalogs.length && (
          <Grid item xs={12}>
            <DynamicQuotationCard
              data-testid="dynamic-card"
              isAutoPx={appliesAutoPxDiscount(quotation)}
              paymentQuantity={maxAllowedPayments}
              unit={usedQuotationUnit}
              catalogCoverages={coverageCatalogs}
              isEditable={false}
              isDisplayedDeductibles={quotation.productId === 'Vehicle'}
              isDisplayedEndorsements={quotation.productId === 'Vehicle'}
              isDisplayedHeader={quotation.productId === 'Vehicle'}
              quotation={quotation}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <BeneficiariesDataCard
            legalBeneficiaries={legalBeneficiary}
            coverageCatalogs={coverageCatalogs}
            quotation={quotation}
            productEndorsements={usedQuotationUnit.coverages}
          />
        </Grid>
        {/* The submit button */}
        <Grid item xs={12}>
          <MessageBox
            data-testid="warning-copy-link"
            variant="info"
            message={
              <span
                className={classes.copyLinkMessage}
                data-testid="customer-name-prompt"
              >
                {`Al hacer clic en "Copiar link de certificación", puedes enviarlo a cualquier otro 
            miembro de tu equipo por WhatsApp o por email, para que realice la certificación digital 
            con el cliente y así culminar el proceso de emisión.`}
              </span>
            }
            hideCloseButton
          />
        </Grid>
        {!isOptionsDetails && (
          <Grid item xs={12} container justifyContent="center">
            <Grid item xs={12} lg={6} container justifyContent="center">
              <div className={wrapperClasses.buttonContainer}>
                <DefaultButton
                  id="copy-sign-link-button"
                  data-testid="copy-sign-link-button"
                  fullWidth
                  onClick={handleCopyLinkClipboard}
                  variant="outlined"
                  customColor={{
                    default: colors.bright_turquoise,
                    hover: colors.onahau,
                    active: colors.white,
                  }}
                >
                  <span className={classes.spaceLinkText}>
                    copiar link de certificación
                  </span>
                </DefaultButton>
              </div>
            </Grid>

            <Grid item xs={12} lg={6} container justifyContent="center">
              <div className={wrapperClasses.buttonContainer}>
                {submitButton(onSubmit, isColectiveQuotation)}
              </div>
            </Grid>

            <Grid item xs={12} lg={6} container justifyContent="center">
              <DownloadDocumentButton data-testid="download-insurance-button" />
            </Grid>
          </Grid>
        )}

        <SnackbarMessage
          data-testid="snackbar-copy-link"
          open={openCopySnack}
          message="Se ha copiado el link de certificación"
          onClose={(): void => setOpenCopySnack(false)}
        />
      </Grid>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  customerData: EmissionDataFields;
  paymentDataFields: PaymentDataFields;
  finalBeneficiaryData: FinalBeneficiary;
  profileExists: boolean;
  quotation: Quotation;
  coverageCatalogs: CoverageCatalog[];
  configuration: ProductConfiguration;
  creditCardData: CreditCardData;
  achData: ACHData;
  currentTotals: TotalsPayload;
  maxAllowedPayments: number;
  evicertiaLink: string;
  legalBeneficiary: LegalBeneficiary[] | undefined;
} => ({
  customerData: state.emissionPolicy.dataFields,
  paymentDataFields: state.emissionPolicy.paymentFields,
  finalBeneficiaryData: state.emissionPolicy.finalBeneficiary,
  profileExists: state.person.personData.isValidPerson,
  quotation: state.quotation.quotation,
  coverageCatalogs: state.coverageCatalog.coverageCatalogs,
  configuration: state.coverageCatalog.productConfig,
  creditCardData: state.creditCardData,
  achData: state.achData,
  legalBeneficiary: state.emissionPolicy.legalBeneficiaries,
  currentTotals: {
    paymentTotal: state.emissionPolicy.paymentTotal,
    electronicPaymentTotal: state.emissionPolicy.electronicPaymentTotal,
    autoPxPaymentTotal: state.emissionPolicy.autoPxPaymentTotal,
  },
  maxAllowedPayments: state.emissionPolicy.paymentFields.paymentQuantity,
  evicertiaLink: state.emissionPolicy.evicertiaLink,
});

const reduxActions = {
  requestCoverageCatalogsAction: requestCoverageCatalogs,
  requestYappyPaymentAction: requestYappyPayment,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  collapsibleCards?: boolean;
  isOptionsDetails?: boolean;
  optionSelected?: number;
  onSubmit: () => void;
}

export default connector(Summary);
